import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';

const PRIMARY = '#B71530';

function apiErr(err, fallback = 'Request failed') {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (Array.isArray(d)) return d.map(x => x.msg || String(x)).join('; ');
  return String(d);
}

const ROLE_OPTIONS = ['reviewer', 'approver', 'admin'];
const ROLE_COLORS  = { admin: 'badge-danger', approver: 'badge-warning', reviewer: 'badge-secondary' };

// ── Small reusable alert ──────────────────────────────────────────────────────
function Flash({ msg }) {
  if (!msg.text) return null;
  return (
    <div className={`alert alert-${msg.type} py-2 mb-3`} style={{ fontSize: '13px' }}>
      {msg.text}
    </div>
  );
}

// ── Password reset modal ──────────────────────────────────────────────────────
function ResetPasswordModal({ user, orgId, onClose, onDone }) {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setErr('Minimum 8 characters'); return; }
    setSaving(true);
    try {
      await client.patch(`/orgs/${orgId}/users/${user.id}`, { new_password: pw });
      onDone(`Password reset for ${user.email}`);
      onClose();
    } catch (error) {
      setErr(apiErr(error, 'Reset failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '400px' }}>
        <div className="modal-content">
          <div className="modal-header py-2">
            <h6 className="modal-title mb-0">Reset Password — {user.email}</h6>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {err && <div className="alert alert-danger py-1 mb-2" style={{ fontSize: '13px' }}>{err}</div>}
              <label className="form-label" style={{ fontSize: '13px' }}>New password</label>
              <input
                type="password"
                className="form-control form-control-sm"
                value={pw}
                onChange={e => { setPw(e.target.value); setErr(''); }}
                autoFocus
                minLength={8}
                placeholder="Min. 8 characters"
              />
            </div>
            <div className="modal-footer py-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-sm"
                style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}
                disabled={saving}>
                {saving ? 'Saving…' : 'Reset Password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────────────
function UsersTab({ org, onNotify }) {
  const [subTab, setSubTab]         = useState('list');
  const [users, setUsers]           = useState([]);
  const [invites, setInvites]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [newUser, setNewUser]       = useState({ email: '', display_name: '', role: 'reviewer', password: '' });
  const [newInvite, setNewInvite]   = useState({ email: '', role: 'admin' });
  const [saving, setSaving]         = useState(false);

  const fetchAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      client.get(`/orgs/${org.id}/users`),
      client.get(`/orgs/${org.id}/invites`),
    ])
      .then(([ur, ir]) => {
        setUsers(ur.data.users || []);
        setInvites(ir.data.invites || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [org.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleActive = async (user) => {
    try {
      await client.patch(`/orgs/${org.id}/users/${user.id}`, { active: !user.active });
      onNotify(`${user.email} ${user.active ? 'deactivated' : 'activated'}`);
      fetchAll();
    } catch (err) { onNotify(apiErr(err), 'danger'); }
  };

  const changeRole = async (user, role) => {
    try {
      await client.patch(`/orgs/${org.id}/users/${user.id}`, { role });
      fetchAll();
    } catch (err) { onNotify(apiErr(err), 'danger'); }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post(`/orgs/${org.id}/users`, newUser);
      onNotify(`User ${newUser.email} added`);
      setNewUser({ email: '', display_name: '', role: 'reviewer', password: '' });
      setSubTab('list');
      fetchAll();
    } catch (err) { onNotify(apiErr(err, 'Add failed'), 'danger'); }
    finally { setSaving(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.post(`/orgs/${org.id}/invites`, newInvite);
      onNotify(`Invite created for ${newInvite.email}`);
      setNewInvite({ email: '', role: 'admin' });
      fetchAll();
    } catch (err) { onNotify(apiErr(err, 'Invite failed'), 'danger'); }
    finally { setSaving(false); }
  };

  const cancelInvite = async (inv) => {
    try {
      await client.delete(`/orgs/${org.id}/invites/${inv.id}`);
      onNotify('Invite cancelled');
      fetchAll();
    } catch (err) { onNotify(apiErr(err), 'danger'); }
  };

  const pendingInvites = invites.filter(i => i.status === 'pending');
  const SUBTABS = [
    { id: 'list',    label: `Users (${users.length})` },
    { id: 'invites', label: `Invites${pendingInvites.length ? ` (${pendingInvites.length} pending)` : ''}` },
    { id: 'add',     label: '+ Add User' },
  ];

  if (loading) return <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-danger" /></div>;

  return (
    <>
      {resetTarget && (
        <ResetPasswordModal
          user={resetTarget}
          orgId={org.id}
          onClose={() => setResetTarget(null)}
          onDone={(msg) => { onNotify(msg); fetchAll(); }}
        />
      )}

      {/* Sub-tabs */}
      <div className="d-flex mb-3" style={{ borderBottom: '1px solid #dee2e6', gap: '4px' }}>
        {SUBTABS.map(t => {
          const isAdd    = t.id === 'add';
          const isActive = subTab === t.id;
          const GREEN    = '#28a745';
          const color    = isAdd ? GREEN : isActive ? PRIMARY : '#495057';
          const border   = isActive
            ? `2px solid ${isAdd ? GREEN : PRIMARY}`
            : '2px solid transparent';
          return (
            <button key={t.id} onClick={() => setSubTab(t.id)}
              style={{
                padding: '5px 14px', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: isActive ? 600 : 400,
                color, borderBottom: border, marginBottom: '-1px',
              }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Users list */}
      {subTab === 'list' && (
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
            <thead className="table-light">
              <tr><th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>Last Login</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-3">No users yet</td></tr>}
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.email}
                  </td>
                  <td>{u.display_name || '–'}</td>
                  <td>
                    <select className="form-control" style={{ width: '95px', fontSize: '11px', padding: '1px 3px' }}
                      value={u.role} onChange={e => changeRole(u, e.target.value)}>
                      {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                    </select>
                  </td>
                  <td>
                    {u.active
                      ? u.last_seen_at
                        ? <span className="badge badge-success" style={{ fontSize: '10px' }}>Active</span>
                        : <span className="badge badge-warning" style={{ fontSize: '10px' }}>Never logged in</span>
                      : <span className="badge badge-secondary" style={{ fontSize: '10px' }}>Inactive</span>
                    }
                  </td>
                  <td style={{ fontSize: '11px', color: '#6c757d' }}>
                    {u.last_seen_at
                      ? new Date(u.last_seen_at).toLocaleString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
                        })
                      : '–'}
                  </td>
                  <td>
                    <div className="d-flex" style={{ gap: '4px' }}>
                      <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '10px', padding: '1px 6px' }}
                        onClick={() => setResetTarget(u)}>Reset PW</button>
                      <button className={`btn btn-sm ${u.active ? 'btn-outline-danger' : 'btn-outline-success'}`}
                        style={{ fontSize: '10px', padding: '1px 6px' }}
                        onClick={() => toggleActive(u)}>
                        {u.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invites */}
      {subTab === 'invites' && (
        <div>
          <form onSubmit={handleInvite} className="mb-3 p-3"
            style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
            <div className="row" style={{ gap: 0 }}>
              <div className="col-md-5">
                <label style={{ fontSize: '12px' }}>Email</label>
                <input type="email" className="form-control form-control-sm" required
                  value={newInvite.email} onChange={e => setNewInvite(p => ({ ...p, email: e.target.value }))}
                  placeholder="admin@company.com" />
              </div>
              <div className="col-md-3">
                <label style={{ fontSize: '12px' }}>Role</label>
                <select className="form-control form-control-sm" value={newInvite.role}
                  onChange={e => setNewInvite(p => ({ ...p, role: e.target.value }))}>
                  {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                </select>
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <button type="submit" className="btn btn-sm ml-2"
                  style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}
                  disabled={saving}>
                  Send Invite
                </button>
              </div>
            </div>
          </form>

          <table className="table table-sm" style={{ fontSize: '12px' }}>
            <thead className="table-light">
              <tr><th>Email</th><th>Role</th><th>Status</th><th>Invited</th><th>By</th><th></th></tr>
            </thead>
            <tbody>
              {invites.length === 0 && <tr><td colSpan="6" className="text-center text-muted py-3">No invites yet</td></tr>}
              {invites.map(inv => (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td><span className="badge badge-secondary" style={{ fontSize: '10px' }}>{inv.role}</span></td>
                  <td>
                    <span className={`badge ${inv.status === 'pending' ? 'badge-warning' : inv.status === 'accepted' ? 'badge-success' : 'badge-secondary'}`}
                      style={{ fontSize: '10px' }}>
                      {inv.status}
                    </span>
                  </td>
                  <td style={{ color: '#6c757d', fontSize: '11px' }}>
                    {inv.invited_at ? new Date(inv.invited_at).toLocaleDateString('en-GB') : '–'}
                  </td>
                  <td style={{ color: '#6c757d', fontSize: '11px' }}>{inv.invited_by || '–'}</td>
                  <td>
                    {inv.status === 'pending' && (
                      <button className="btn btn-sm btn-outline-danger" style={{ fontSize: '10px', padding: '1px 6px' }}
                        onClick={() => cancelInvite(inv)}>Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add user directly */}
      {subTab === 'add' && (
        <form onSubmit={handleAddUser} style={{ maxWidth: '460px' }}>
          <div className="mb-3">
            <label className="form-label" style={{ fontSize: '12px' }}>Email *</label>
            <input type="email" className="form-control form-control-sm" required
              value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
          </div>
          <div className="mb-3">
            <label className="form-label" style={{ fontSize: '12px' }}>Display Name</label>
            <input type="text" className="form-control form-control-sm"
              value={newUser.display_name} onChange={e => setNewUser(p => ({ ...p, display_name: e.target.value }))} />
          </div>
          <div className="mb-3">
            <label className="form-label" style={{ fontSize: '12px' }}>Role</label>
            <select className="form-control form-control-sm" value={newUser.role}
              onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label" style={{ fontSize: '12px' }}>Temporary Password *</label>
            <input type="password" className="form-control form-control-sm" required minLength={8}
              value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))}
              placeholder="Min. 8 characters" />
            <div className="form-text" style={{ fontSize: '11px' }}>User should change this on first login via Profile</div>
          </div>
          <button type="submit" className="btn btn-sm mr-2"
            style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}
            disabled={saving}>
            {saving ? 'Adding…' : 'Add User'}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary"
            onClick={() => setSubTab('list')}>Cancel</button>
        </form>
      )}
    </>
  );
}

// ── Branding tab ──────────────────────────────────────────────────────────────
function BrandingTab({ org, onNotify }) {
  const [form, setForm] = useState({
    display_name: org.display_name || '',
    primary_color: org.primary_color || '#B71530',
  });
  const [logoFile, setLogoFile]       = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [uploading, setUploading]     = useState({ logo: false, favicon: false });

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await client.patch(`/orgs/${org.id}`, form);
      onNotify('Branding saved — reload the page to see changes');
    } catch (err) { onNotify(apiErr(err), 'danger'); }
  };

  const handleUpload = async (kind) => {
    const file = kind === 'logo' ? logoFile : faviconFile;
    if (!file) return;
    setUploading(p => ({ ...p, [kind]: true }));
    const fd = new FormData();
    fd.append('file', file);
    try {
      await client.post(`/orgs/${org.id}/${kind}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onNotify(`${kind === 'logo' ? 'Logo' : 'Favicon'} uploaded — reload to see it`);
      if (kind === 'logo')    setLogoFile(null);
      else                    setFaviconFile(null);
    } catch (err) { onNotify(apiErr(err, 'Upload failed'), 'danger'); }
    finally { setUploading(p => ({ ...p, [kind]: false })); }
  };

  return (
    <div style={{ maxWidth: '520px' }}>
      {/* Display name + colour */}
      <form onSubmit={handleSave} className="mb-4">
        <h6 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>Identity</h6>
        <div className="mb-3">
          <label className="form-label" style={{ fontSize: '12px' }}>Display Name (sidebar &amp; navbar)</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={form.display_name}
            onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))}
            placeholder="My Company AP Automation"
          />
        </div>
        <div className="mb-3">
          <label className="form-label" style={{ fontSize: '12px' }}>Primary Colour</label>
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <input
              type="color"
              className="form-control"
              style={{ width: '52px', height: '32px', padding: '2px', cursor: 'pointer' }}
              value={form.primary_color}
              onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))}
            />
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: '110px', fontFamily: 'monospace' }}
              value={form.primary_color}
              onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))}
            />
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%',
              backgroundColor: form.primary_color, border: '1px solid #dee2e6',
              flexShrink: 0,
            }} />
          </div>
        </div>
        <button type="submit" className="btn btn-sm"
          style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}>
          Save
        </button>
      </form>

      <hr style={{ margin: '20px 0' }} />

      {/* Logo */}
      <div className="mb-4">
        <h6 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
          Logo <span style={{ fontSize: '11px', fontWeight: 400, color: '#6c757d' }}>PNG or JPG — replaces text name in sidebar</span>
        </h6>
        <input
          type="file"
          className="form-control-file mb-2"
          accept=".png,.jpg,.jpeg"
          onChange={e => setLogoFile(e.target.files[0])}
          key={logoFile ? 'set' : 'empty'}
        />
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => handleUpload('logo')}
          disabled={!logoFile || uploading.logo}
        >
          {uploading.logo ? 'Uploading…' : 'Upload Logo'}
        </button>
      </div>

      {/* Favicon */}
      <div>
        <h6 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
          Favicon <span style={{ fontSize: '11px', fontWeight: 400, color: '#6c757d' }}>ICO or PNG — shown in browser tab</span>
        </h6>
        <input
          type="file"
          className="form-control-file mb-2"
          accept=".ico,.png"
          onChange={e => setFaviconFile(e.target.files[0])}
          key={faviconFile ? 'set' : 'empty'}
        />
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => handleUpload('favicon')}
          disabled={!faviconFile || uploading.favicon}
        >
          {uploading.favicon ? 'Uploading…' : 'Upload Favicon'}
        </button>
      </div>
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────
function OverviewTab({ org, onNotify, onRefresh }) {
  const [form, setForm] = useState({
    name: org.name,
    subdomain: org.subdomain,
    display_name: org.display_name || '',
  });

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await client.patch(`/orgs/${org.id}`, { name: form.name, display_name: form.display_name });
      onNotify('Organization details saved');
      onRefresh();
    } catch (err) { onNotify(apiErr(err), 'danger'); }
  };

  return (
    <div style={{ maxWidth: '480px' }}>
      <form onSubmit={handleSave}>
        <div className="mb-3">
          <label className="form-label" style={{ fontSize: '12px' }}>Organization Name</label>
          <input type="text" className="form-control form-control-sm" required
            value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
        </div>
        <div className="mb-3">
          <label className="form-label" style={{ fontSize: '12px' }}>Subdomain</label>
          <input type="text" className="form-control form-control-sm" readOnly
            value={form.subdomain} style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }} />
          <div className="form-text" style={{ fontSize: '11px' }}>
            Access at <code>{form.subdomain}.yourdomain.com</code> — cannot be changed after creation
          </div>
        </div>
        <div className="mb-3">
          <label className="form-label" style={{ fontSize: '12px' }}>Display Name (shown in UI)</label>
          <input type="text" className="form-control form-control-sm"
            value={form.display_name} onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))}
            placeholder={form.name} />
        </div>
        <button type="submit" className="btn btn-sm"
          style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}>
          Save Changes
        </button>
      </form>
    </div>
  );
}

// ── Main AppAdmin page ────────────────────────────────────────────────────────
export default function AppAdmin() {
  const [orgs, setOrgs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [detailTab, setDetailTab] = useState('users');
  const [msg, setMsg]             = useState({ text: '', type: 'success' });
  const [showNew, setShowNew]     = useState(false);
  const [newOrg, setNewOrg]       = useState({ name: '', subdomain: '', display_name: '', primary_color: '#B71530' });

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: 'success' }), 4000);
  };

  const fetchOrgs = useCallback(() => {
    setLoading(true);
    client.get('/orgs/')
      .then(r => {
        const list = r.data.organizations || [];
        setOrgs(list);
        // Keep selectedOrg in sync after refresh
        setSelectedOrg(prev => prev ? (list.find(o => o.id === prev.id) || null) : null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const handleSelectOrg = (org) => {
    setSelectedOrg(org);
    setDetailTab('users');
  };

  const handleToggleActive = async (org, e) => {
    e.stopPropagation();
    try {
      await client.patch(`/orgs/${org.id}`, { active: !org.active });
      showMsg(`"${org.name}" ${org.active ? 'deactivated' : 'activated'}`);
      fetchOrgs();
    } catch (err) { showMsg(apiErr(err), 'danger'); }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      await client.post('/orgs/', newOrg);
      showMsg(`"${newOrg.name}" created`);
      setNewOrg({ name: '', subdomain: '', display_name: '', primary_color: '#B71530' });
      setShowNew(false);
      fetchOrgs();
    } catch (err) { showMsg(apiErr(err, 'Create failed'), 'danger'); }
  };

  const DETAIL_TABS = [
    { id: 'users',    label: `Users (${selectedOrg?.user_count ?? 0})` },
    { id: 'branding', label: 'Branding' },
    { id: 'overview', label: 'Settings' },
  ];

  return (
    <div>
      <Flash msg={msg} />

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 style={{ margin: 0, fontWeight: 600, fontSize: '16px' }}>
          Organizations
          <span className="badge badge-secondary ml-2" style={{ fontSize: '12px', fontWeight: 400 }}>
            {orgs.length}
          </span>
        </h5>
        <button
          className="btn btn-sm"
          style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}
          onClick={() => setShowNew(p => !p)}
        >
          <i className={`bi bi-${showNew ? 'dash' : 'plus'} mr-1`} />
          {showNew ? 'Cancel' : 'New Organization'}
        </button>
      </div>

      {/* ── New org form ────────────────────────────────────────────────── */}
      {showNew && (
        <div className="card mb-3" style={{ border: `1px solid ${PRIMARY}` }}>
          <div className="card-header fw-semibold" style={{ fontSize: '13px', backgroundColor: '#fff8f8' }}>
            New Organization
          </div>
          <div className="card-body">
            <form onSubmit={handleCreateOrg}>
              <div className="row" style={{ gap: 0 }}>
                <div className="col-md-3">
                  <label className="form-label mb-1" style={{ fontSize: '12px' }}>Organization Name *</label>
                  <input type="text" className="form-control form-control-sm" required
                    value={newOrg.name} onChange={e => setNewOrg(p => ({ ...p, name: e.target.value }))}
                    placeholder="Acme Corp" />
                </div>
                <div className="col-md-2">
                  <label className="form-label mb-1" style={{ fontSize: '12px' }}>Subdomain *</label>
                  <input type="text" className="form-control form-control-sm" required
                    value={newOrg.subdomain}
                    onChange={e => setNewOrg(p => ({ ...p, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                    placeholder="acme" />
                </div>
                <div className="col-md-3">
                  <label className="form-label mb-1" style={{ fontSize: '12px' }}>Display Name</label>
                  <input type="text" className="form-control form-control-sm"
                    value={newOrg.display_name} onChange={e => setNewOrg(p => ({ ...p, display_name: e.target.value }))}
                    placeholder="Acme AP Automation" />
                </div>
                <div className="col-md-2">
                  <label className="form-label mb-1" style={{ fontSize: '12px' }}>Primary Colour</label>
                  <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <input type="color" style={{ width: '36px', height: '30px', padding: '1px', cursor: 'pointer' }}
                      value={newOrg.primary_color}
                      onChange={e => setNewOrg(p => ({ ...p, primary_color: e.target.value }))} />
                    <input type="text" className="form-control form-control-sm"
                      style={{ fontFamily: 'monospace', fontSize: '11px' }}
                      value={newOrg.primary_color}
                      onChange={e => setNewOrg(p => ({ ...p, primary_color: e.target.value }))} />
                  </div>
                </div>
                <div className="col-md-2 d-flex align-items-end">
                  <button type="submit" className="btn btn-sm w-100"
                    style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}>
                    Create
                  </button>
                </div>
              </div>
              {newOrg.subdomain && (
                <div className="mt-1" style={{ fontSize: '11px', color: '#6c757d' }}>
                  Will be accessed at <code>{newOrg.subdomain}.yourdomain.com</code>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ── Split layout ─────────────────────────────────────────────────── */}
      <div className="row" style={{ margin: 0 }}>

        {/* Left: org list */}
        <div className={`col-md-${selectedOrg ? '4' : '12'}`} style={{ paddingLeft: 0, paddingRight: selectedOrg ? '12px' : 0 }}>
          <div className="card" style={{ height: '100%' }}>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-danger spinner-border-sm" />
                </div>
              ) : (
                <div>
                  {orgs.map(org => {
                    const isSelected = selectedOrg?.id === org.id;
                    return (
                      <div
                        key={org.id}
                        onClick={() => handleSelectOrg(org)}
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #f0f0f0',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#fff8f8' : '#fff',
                          borderLeft: isSelected ? `3px solid ${PRIMARY}` : '3px solid transparent',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = '#fff'; }}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '13px', color: '#212529' }}>
                              {org.name}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '1px' }}>
                              <code>{org.subdomain}</code>
                              &nbsp;·&nbsp;
                              <span style={{ color: org.primary_color }}>■</span>
                              &nbsp;·&nbsp;
                              {org.user_count} user{org.user_count !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <div className="d-flex align-items-center" style={{ gap: '6px', flexShrink: 0, marginLeft: '8px' }}>
                            <span className={`badge ${org.active ? 'badge-success' : 'badge-secondary'}`}
                              style={{ fontSize: '10px' }}>
                              {org.active ? 'Active' : 'Off'}
                            </span>
                            <button
                              className={`btn btn-sm ${org.active ? 'btn-outline-danger' : 'btn-outline-success'}`}
                              style={{ fontSize: '10px', padding: '1px 6px' }}
                              onClick={(e) => handleToggleActive(org, e)}
                            >
                              {org.active ? 'Disable' : 'Enable'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {orgs.length === 0 && (
                    <div className="text-center text-muted py-5" style={{ fontSize: '13px' }}>
                      No organizations yet. Click "New Organization" to add one.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: detail panel */}
        {selectedOrg && (
          <div className="col-md-8" style={{ paddingLeft: 0, paddingRight: 0 }}>
            <div className="card" style={{ height: '100%' }}>
              {/* Panel header */}
              <div className="card-header d-flex justify-content-between align-items-center py-2"
                style={{ backgroundColor: '#f8f9fa' }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '14px' }}>{selectedOrg.name}</span>
                  <span className="text-muted ml-2" style={{ fontSize: '12px' }}>
                    <code>{selectedOrg.subdomain}</code>
                  </span>
                </div>
                <button type="button" className="close" style={{ fontSize: '18px' }}
                  onClick={() => setSelectedOrg(null)}>
                  <span>&times;</span>
                </button>
              </div>

              {/* Tabs */}
              <div style={{ padding: '0 16px', backgroundColor: '#fff' }}>
                <ul className="nav app-tabs">
                  {DETAIL_TABS.map(t => (
                    <li className="nav-item" key={t.id}>
                      <button
                        className={`nav-link${detailTab === t.id ? ' active' : ''}`}
                        onClick={() => setDetailTab(t.id)}
                      >
                        {t.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tab content */}
              <div className="card-body" style={{ padding: '16px' }}>
                {detailTab === 'users' && (
                  <UsersTab org={selectedOrg} onNotify={showMsg} />
                )}
                {detailTab === 'branding' && (
                  <BrandingTab org={selectedOrg} onNotify={showMsg} />
                )}
                {detailTab === 'overview' && (
                  <OverviewTab org={selectedOrg} onNotify={showMsg} onRefresh={fetchOrgs} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
